/**
 * CUSTOMIZATION: Presidio theme override — do not merge Presidio imports into the DIAS base file.
 */
import { Inject, Injectable, Injector } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { isEqual, uniq } from 'lodash-es';
import { Observable, combineLatest, from, identity, merge, of, pipe } from 'rxjs';
import {
  concatMap,
  debounce,
  debounceTime,
  delay,
  distinct,
  distinctUntilChanged,
  filter,
  ignoreElements,
  map,
  mergeMap,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  toArray,
} from 'rxjs/operators';

import { PRICE_UPDATE } from 'ish-core/configurations/injection-keys';
import { CategoryView } from 'ish-core/models/category-view/category-view.model';
import { CategoryHelper } from 'ish-core/models/category/category.model';
import { AddLineItemType } from 'ish-core/models/line-item/line-item.model';
import { PriceItemHelper } from 'ish-core/models/price-item/price-item.helper';
import { ProductListingID } from 'ish-core/models/product-listing/product-listing.model';
import { ProductCompletenessLevel, ProductHelper } from 'ish-core/models/product/product.model';
import { CustomSuggestTerm } from 'ish-core/models/suggest-term/custom-suggest-term.model';
import { ofCategoryUrl } from 'ish-core/routing/category/category.route';
import { getSparqueConfig } from 'ish-core/store/core/configuration';
import { RecommendationsParams } from 'ish-core/models/recommendations/recommendations.model';
import { selectRouteParam, selectUrl } from 'ish-core/store/core/router';
import { addItemsToBasket, addProductToBasket } from 'ish-core/store/customer/basket';
import { getPGID, getPriceDisplayType } from 'ish-core/store/customer/user';
// CUSTOMIZATION: quote-only cart guard (FR13.7.5)
import { PresidioQuoteCartGuardService } from 'src/app/extensions/quoting/services/presidio-quote-cart-guard/presidio-quote-cart-guard.service';
import {
  fetchSpecificCategory,
  getCategory,
  getCategoryIdByRefId,
  getCategoryProductLinks,
  getNavigationCategories,
  getNavigationCategoryTree,
  getSelectedCategory,
  getSingleNavigationCategories,
  loadCategoryByRef,
  loadCategoryLinks,
  loadCategoryTree,
  loadSingleCategoryByRef,
  loadSingleCategoryByUniqueId,
  loadTopLevelCategories,
} from 'ish-core/store/shopping/categories';
import { getAvailableFilter, loadFilterForCategory } from 'ish-core/store/shopping/filter';
import { getProductInventory } from 'ish-core/store/shopping/product-inventory';
import {
  getProductListingLoading,
  getProductListingView,
  getProductListingViewType,
  loadMoreProducts,
} from 'ish-core/store/shopping/product-listing';
import { loadProductPrices } from 'ish-core/store/shopping/product-prices';
import { getProductPrice } from 'ish-core/store/shopping/product-prices/product-prices.selectors';
import {
  getFailedProducts,
  getProduct,
  getProductForSparque,
  getProductLinks,
  getProductParts,
  getProductVariationCount,
  getProductVariations,
  loadProduct,
  loadProductIfNotLoaded,
  loadProductLinks,
  loadProductParts,
  loadProductVariationsIfNotLoaded,
} from 'ish-core/store/shopping/products';
import { getPromotion, getPromotions, loadPromotion } from 'ish-core/store/shopping/promotions';
import { getRecommendationsForStrategy, recommendationsActions } from 'ish-core/store/shopping/recommendations';
import {
  customSuggestSearch,
  getCustomSuggestSearchResults,
  getSearchTerm,
  getSearchedTerms,
  getSuggestSearchLoading,
  getSuggestSearchResults,
  removeSuggestions,
  suggestSearch,
} from 'ish-core/store/shopping/search';
import { getWarranty, getWarrantyError, getWarrantyLoading, warrantyActions } from 'ish-core/store/shopping/warranties';
import { toObservable } from 'ish-core/utils/functions';
import { InjectSingle } from 'ish-core/utils/injection';
import { mapToProperty, whenFalsy, whenTruthy } from 'ish-core/utils/operators';

@Injectable({ providedIn: 'root' })
export class ShoppingFacade {
  constructor(
    private store: Store,
    private injector: Injector,
    @Inject(PRICE_UPDATE) private priceUpdate: InjectSingle<typeof PRICE_UPDATE>
  ) {}

  /** Lazy resolve avoids ShoppingFacade ↔ quote-guard construction cycles. */
  private quoteCartGuard(): PresidioQuoteCartGuardService {
    return this.injector.get(PresidioQuoteCartGuardService);
  }

  // CATEGORY

  selectedCategory$ = this.store.pipe(select(getSelectedCategory));
  selectedCategoryId$ = this.store.pipe(select(selectRouteParam('categoryUniqueId')));

  category$(uniqueId: string) {
    return this.store.pipe(select(getCategory(uniqueId)));
  }

  fetchSpecificCategory$(categoryId: string) {
    this.store.dispatch(fetchSpecificCategory({ categoryId }));
  }

  categoryIdByRefId$(categoryRefId: string) {
    this.store.dispatch(loadCategoryByRef({ categoryId: categoryRefId }));
    return this.store.pipe(select(getCategoryIdByRefId(categoryRefId)));
  }

  navigationCategories$(uniqueId?: string) {
    if (!uniqueId) {
      this.store.dispatch(loadTopLevelCategories());
    }
    return this.store.pipe(
      select(getNavigationCategories(uniqueId)),
      // prevent to display an empty navigation bar after login/logout);
      filter(categories => !!categories?.length)
    );
  }

  navigationCategoryTree$(categoryRef: string, depth: number) {
    this.store.dispatch(loadCategoryTree({ categoryRef, depth }));
    return this.store.pipe(select(getNavigationCategoryTree(categoryRef, depth)));
  }

  // PRODUCT

  selectedProductId$ = this.store.pipe(select(selectRouteParam('sku')));

  product$(sku: Observable<string> | string, level: ProductCompletenessLevel | true) {
    const completenessLevel = level === true ? ProductCompletenessLevel.Detail : level;
    return toObservable(sku).pipe(
      tap(plainSKU => {
        if (level === true) {
          this.store.dispatch(loadProduct({ sku: plainSKU }));
        } else {
          this.store.dispatch(loadProductIfNotLoaded({ sku: plainSKU, level }));
        }
      }),
      switchMap(plainSKU =>
        this.store.pipe(
          select(getProduct(plainSKU)),
          startWith(undefined),
          pairwise(),
          tap(([prev, curr]) => {
            if (
              ProductHelper.isReadyForDisplay(prev, completenessLevel) &&
              !ProductHelper.isReadyForDisplay(curr, completenessLevel)
            ) {
              level === true
                ? this.store.dispatch(loadProduct({ sku: plainSKU }))
                : this.store.dispatch(loadProductIfNotLoaded({ sku: plainSKU, level }));
            }
          }),
          map(([, curr]) => curr),
          filter(p => ProductHelper.isReadyForDisplay(p, completenessLevel))
        )
      )
    );
  }

  failedProducts$ = this.store.pipe(select(getFailedProducts));

  // remove all SKUs from the productSKUs that are also contained in the failed products
  excludeFailedProducts$(productSKUs: Observable<string[]> | string[]) {
    return combineLatest([toObservable(productSKUs), this.store.pipe(select(getFailedProducts))]).pipe(
      distinctUntilChanged<[string[], string[]]>(isEqual),
      map(([skus, failed]) => skus.filter(sku => !failed.includes(sku)))
    );
  }

  /* DIAS Customization Start */
  /**
   * Like excludeFailedProducts$, but also loads every product of the list - not only those whose tile is rendered.
   *
   * Carousels render their tiles lazily, so a product on a slide that was never visible was never requested and
   * could not fail: one hidden by the customer segment's catalog view stayed in the list until swiped into view.
   * Loading goes through product$, which also requests the products again after the PGID changed (login).
   *
   * With a `limit` (a carousel that shows only the first n products of its list), only the products needed to fill
   * it are loaded: the first `limit` SKUs, and the next SKU of the list for every one of them that failed. The
   * products emitted are the same as without the limit, cut to `limit` - the rest of the list is not requested.
   */
  loadProductsOfList$(productSKUs: string[], limit?: number): Observable<string[]> {
    const skus = (productSKUs ?? []).filter(sku => !!sku);
    if (!(limit > 0) || limit >= skus.length) {
      return merge(
        ...skus.map(sku => this.product$(sku, ProductCompletenessLevel.List).pipe(ignoreElements())),
        this.excludeFailedProducts$(skus)
      );
    }
    // the shortest start of the list that holds `limit` products which did not fail
    const neededSkus$ = this.store.pipe(
      select(getFailedProducts),
      map(failed => {
        let end = 0;
        for (let found = 0; end < skus.length && found < limit; end++) {
          if (!failed.includes(skus[end])) {
            found++;
          }
        }
        return skus.slice(0, end);
      }),
      distinctUntilChanged<string[]>(isEqual),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    return merge(
      // every SKU is loaded once, when it becomes needed, and then stays subscribed like in the unlimited list
      neededSkus$.pipe(
        mergeMap(needed => needed),
        distinct(),
        mergeMap(sku => this.product$(sku, ProductCompletenessLevel.List).pipe(ignoreElements()))
      ),
      neededSkus$.pipe(switchMap(needed => this.excludeFailedProducts$(needed)))
    );
  }
  /* DIAS Customization End */

  productPrices$(sku: Observable<string> | string, fresh = false) {
    return toObservable(sku).pipe(
      whenTruthy(),
      switchMap(plainSKU =>
        combineLatest([
          this.store.pipe(
            select(getProductPrice(plainSKU)),
            // reset state when updates are forced
            this.priceUpdate === 'always' || fresh ? startWith(undefined) : identity,
            distinctUntilChanged(),
            tap(prices => {
              if (!prices) {
                this.store.dispatch(loadProductPrices({ skus: [plainSKU] }));
              }
            }),
            whenTruthy()
          ),
          this.store.pipe(select(getPriceDisplayType)),
        ]).pipe(map(args => PriceItemHelper.selectPricing(...args)))
      )
    );
  }

  productInventory$(sku: Observable<string> | string) {
    return toObservable(sku).pipe(
      whenTruthy(),
      switchMap(plainSKU => this.store.pipe(select(getProductInventory(plainSKU)), distinctUntilChanged()))
    );
  }

  private lazyLoadVariations(sku: Observable<string> | string) {
    return this.product$(sku, ProductCompletenessLevel.List).pipe(
      whenTruthy(),
      filter(ProductHelper.isMasterProduct),
      mapToProperty('sku'),
      distinctUntilChanged(),
      tap(masterSku => {
        this.store.dispatch(loadProductVariationsIfNotLoaded({ sku: masterSku }));
      })
    );
  }

  productVariations$(sku: Observable<string> | string) {
    return this.lazyLoadVariations(sku).pipe(
      switchMap(varSku => this.store.pipe(select(getProductVariations(varSku))))
    );
  }

  productVariationCount$(sku: Observable<string> | string) {
    return this.lazyLoadVariations(sku).pipe(
      switchMap(plainSKU => this.store.pipe(select(getProductVariationCount(plainSKU))))
    );
  }

  // CHECKOUT

  addProductToBasket(sku: string, quantity: number, warrantySku?: string) {
    // CUSTOMIZATION: FR13.7.5 — quote in cart → Saved for later confirm (no mixed cart)
    if (!this.quoteCartGuard().tryCatalogAddToBasket([{ sku, quantity }])) {
      return;
    }
    this.store.dispatch(addProductToBasket({ sku, quantity, warrantySku }));
  }

  addProductsToBasket(items: AddLineItemType[]) {
    // CUSTOMIZATION: FR13.7.5 — quote in cart → Saved for later confirm (no mixed cart)
    if (
      !this.quoteCartGuard().tryCatalogAddToBasket(
        (items || []).map(item => ({ sku: item.sku, quantity: item.quantity }))
      )
    ) {
      return;
    }
    this.store.dispatch(addItemsToBasket({ items }));
  }

  // PRODUCT LISTING

  productListingView$(productListingId: Observable<ProductListingID> | ProductListingID) {
    return toObservable(productListingId).pipe(
      whenTruthy(),
      tap(id => this.store.dispatch(loadMoreProducts({ id }))),
      switchMap(id => this.store.pipe(select(getProductListingView(id))))
    );
  }

  productListingViewType$ = this.store.pipe(select(getProductListingViewType));
  productListingLoading$ = this.store.pipe(select(getProductListingLoading));

  loadMoreProducts(id: ProductListingID, page: number) {
    this.store.dispatch(loadMoreProducts({ id, page }));
  }

  // PRODUCT LINKS

  productLinks$(sku: Observable<string> | string) {
    return toObservable(sku).pipe(
      whenTruthy(),
      switchMap(plainSKU =>
        this.store.pipe(
          select(getProductLinks(plainSKU)),
          tap(links => {
            if (!links) {
              this.store.dispatch(loadProductLinks({ sku: plainSKU }));
            }
          })
        )
      )
    );
  }

  // PRODUCT RETAIL SET / BUNDLES

  productParts$(sku: Observable<string> | string) {
    return toObservable(sku).pipe(
      whenTruthy(),
      tap(plainSKU => {
        this.store.dispatch(loadProductParts({ sku: plainSKU }));
      }),
      switchMap(plainSKU => this.store.pipe(select(getProductParts(plainSKU))))
    );
  }

  // SEARCH
  recentSearchTerms$ = this.store.pipe(select(getSearchedTerms));
  searchTerm$ = this.store.pipe(select(getSearchTerm));

  suggestResults$(searchTerm: Observable<string>) {
    return searchTerm.pipe(
      distinctUntilChanged(),
      debounceTime(400),
      filter(term => term.length > 2),
      tap(term => this.store.dispatch(suggestSearch({ searchTerm: term }))),
      switchMap(() => this.store.pipe(select(getSuggestSearchResults)))
    );
  }

  clearSuggestSearchSuggestions() {
    this.store.dispatch(removeSuggestions());
  }

  searchSuggestLoading$ = this.store.pipe(select(getSuggestSearchLoading));

  searchLoading$ = this.store.pipe(select(getProductListingLoading));

  searchItemsCount$ = this.searchTerm$.pipe(
    debounce(() => this.store.pipe(select(getProductListingLoading), whenFalsy())),
    switchMap(term =>
      this.store.pipe(
        select(getProductListingView({ type: 'search', value: term })),
        map(view => view.itemCount)
      )
    )
  );

  // RECOMMENDATIONS

  productRecommendations$(recommendationsParams: RecommendationsParams) {
    this.store.dispatch(recommendationsActions.loadProductRecommendations({ recommendationsParams }));
    return this.store.pipe(select(getRecommendationsForStrategy(recommendationsParams.strategy)));
  }

  // FILTER

  currentFilter$(withCategoryFilter: boolean) {
    return this.store.pipe(
      select(getAvailableFilter),
      whenTruthy(),
      map(x => (withCategoryFilter ? x : { ...x, filter: x.filter?.filter(f => f.id !== 'CategoryUUIDLevelMulti') }))
    );
  }

  // PROMOTIONS

  promotion$(promotionId: string) {
    this.store.dispatch(loadPromotion({ promoId: promotionId }));
    return this.store.pipe(select(getPromotion(promotionId)));
  }

  promotions$(promotionIds: string[]) {
    promotionIds.forEach(promotionId => {
      this.store.dispatch(loadPromotion({ promoId: promotionId }));
    });
    return this.store.pipe(select(getPromotions(promotionIds)));
  }

  // WARRANTIES

  warrantyById$(warrantyId: string) {
    this.store.dispatch(warrantyActions.loadWarranty({ warrantyId }));
    return this.store.pipe(select(getWarranty(warrantyId)));
  }

  warrantyError$ = this.store.pipe(select(getWarrantyError));
  warrantyLoading$ = this.store.pipe(select(getWarrantyLoading));
  /* DIAS CUSTOMIZATION STARTS */

  customSearchResults$(searchTerm: Observable<string>) {
    return searchTerm.pipe(
      tap(term => this.store.dispatch(customSuggestSearch({ searchTerm: term }))),
      switchMap(term => this.store.pipe(select(getCustomSuggestSearchResults(term))))
    );
  }

  singleNavigationCategories$(categoryRefId: string) {
    /* DIAS Customization Start */
    // (re)load the tree for every PGID, like getSingleCategoryByUniqueId$: categories are reset when the PGID changes
    // and ICM catalog views decide per customer segment what the tree contains. Triggered by the PGID itself rather
    // than by the login state, so the request always goes out after the reset and with the new PGID
    return this.store.pipe(
      select(getPGID),
      distinctUntilChanged(),
      tap(() => this.store.dispatch(loadSingleCategoryByRef({ categoryId: categoryRefId }))),
      switchMap(() =>
        this.store.pipe(
          select(getCategoryIdByRefId(categoryRefId)),
          whenTruthy(),
          switchMap(uniqueId =>
            this.store.pipe(
              select(getSingleNavigationCategories(uniqueId)),
              filter(category => !!category)
            )
          )
        )
      )
    );
    /* DIAS Customization End */
  }
  getCategoryByRefId$(categoryRefId: string) {
    this.store.dispatch(loadSingleCategoryByRef({ categoryId: categoryRefId }));
    return this.store
      .pipe(select(getCategoryIdByRefId(categoryRefId)))
      .pipe(switchMap(uniqueId => this.store.pipe(select(getCategory(uniqueId)))));
  }

  getSingleCategoryByUniqueId$(uniqueId: string) {
    /* DIAS Customization Start */
    // (re)load the category for every PGID, like the main navigation: categories are reset when the PGID changes
    // (e.g. on login) and ICM catalog views decide per customer segment whether it exists - a hidden category
    // fails to load and stays undefined. Reloading per PGID (not only after it was loaded before) also covers a
    // category that failed for the previous customer but is visible to the new one.
    return this.store.pipe(
      select(getPGID),
      distinctUntilChanged(),
      tap(() => this.store.dispatch(loadSingleCategoryByUniqueId({ uniqueId }))),
      switchMap(() => this.store.pipe(select(getCategory(uniqueId))))
    );
    /* DIAS Customization End */
  }

  /* DIAS Customization Start */
  /**
   * The given category ids that the current customer may see, in their original order - loaded (again) for every
   * PGID via getSingleCategoryByUniqueId$. A category hidden by the customer segment's catalog view never loads
   * and is left out, the way the main navigation leaves it out.
   */
  categoryIdsForCustomer$(uniqueIds: string[]): Observable<string[]> {
    const ids = (uniqueIds ?? []).filter(id => !!id);
    if (!ids.length) {
      return of([]);
    }
    return combineLatest(ids.map(id => this.getSingleCategoryByUniqueId$(id))).pipe(
      map(categories => ids.filter((_, index) => !!categories[index])),
      distinctUntilChanged<string[]>(isEqual)
    );
  }

  /**
   * The given categories as the current customer sees them, in their original order and without duplicates - each
   * one (re)loaded for every PGID via getSingleCategoryByUniqueId$. A category hidden by the customer segment's
   * catalog view never loads and is undefined at its position, so callers decide how to treat a missing one.
   *
   * Categories in `linkOnlyIds` are only shown as a link (name and uniqueId). They are not requested one by one but
   * taken from the sub categories of their parent category, which the category details call of the parent returns
   * for the current customer - one request per parent instead of one per category. Such a category is undefined at
   * its position while its parent is not loaded, or when the parent does not list it. It is not completely loaded,
   * so the category page still requests it when the link is followed.
   */
  categoriesForCustomer$(uniqueIds: string[], linkOnlyIds: string[] = []): Observable<CategoryView[]> {
    const ids = uniq((uniqueIds ?? []).filter(id => !!id));
    if (!ids.length) {
      return of([]);
    }
    const separator = CategoryHelper.uniqueIdSeparator;
    // one shared stream per requested category, so a parent of several links, or also shown itself, is loaded once
    const requested: Record<string, Observable<CategoryView>> = {};
    const request$ = (id: string) =>
      (requested[id] ??= this.getSingleCategoryByUniqueId$(id).pipe(shareReplay({ bufferSize: 1, refCount: true })));

    return combineLatest(
      ids.map(id => {
        if (!linkOnlyIds.includes(id) || !id.includes(separator)) {
          return request$(id);
        }
        return combineLatest([
          request$(id.slice(0, id.lastIndexOf(separator))),
          this.store.pipe(select(getCategory(id))),
        ]).pipe(
          map(([parent, category]) =>
            CategoryHelper.isCategoryCompletelyLoaded(parent) && parent.children.includes(id) ? category : undefined
          )
        );
      })
    );
  }
  /* DIAS Customization End */

  loadFilterForCategory(id: ProductListingID) {
    this.store.dispatch(loadFilterForCategory({ uniqueId: id.value }));
  }

  loadFilterOnRouteChange() {
    this.store
      .pipe(
        ofCategoryUrl(),
        concatLatestFrom(() => [
          this.store.pipe(select(selectRouteParam('categoryUniqueId'))),
          this.store.pipe(select(selectRouteParam('categoryRefId'))),
        ]),
        switchMap(([_, categoryID, categoryRefId]) =>
          combineLatest([of(categoryID), this.store.pipe(select(getCategoryIdByRefId(categoryRefId)))])
        ),
        take(1)
      )
      .subscribe(([categoryID, categoryRefId]) => {
        this.loadFilterForCategory({ type: 'category', value: categoryID ?? categoryRefId });
      });
  }

  getSearchResult$(searchTerm: Observable<string>) {
    return combineLatest([searchTerm, this.store.pipe(select(getSparqueConfig))]).pipe(
      distinctUntilChanged(isEqual),
      switchMap(([searchTearm, sparqueConfig]) => {
        if (sparqueConfig) {
          return of(searchTearm).pipe(
            filter(term => term.length > 2),
            tap(term => this.store.dispatch(suggestSearch({ searchTerm: term }))),
            switchMap(() => this.store.pipe(select(getSuggestSearchResults))),
            filter(suggestion => suggestion !== undefined),
            switchMap(sparqueSuggest => {
              const skus = sparqueSuggest?.products ?? [];
              if (!skus.length) {
                return of([]);
              }
              return from(skus).pipe(
                delay(5),
                concatMap(sku => this.store.pipe(select(getProductForSparque(sku)), take(1))),
                toArray()
              );
            }),
            map(suggestProducts =>
              suggestProducts.map(
                product =>
                  ({
                    id: product.sku,
                    image: product?.images[2]?.effectiveUrl,
                    term: product.name,
                    type: product.type,
                  }) as CustomSuggestTerm
              )
            )
          );
        } else {
          return of(searchTearm).pipe(
            tap(term => this.store.dispatch(customSuggestSearch({ searchTerm: term }))),
            switchMap(term => this.store.pipe(select(getCustomSuggestSearchResults(term)))),
            map(data => data.filter(data => data.type === 'product'))
          );
        }
      })
    );
  }
  getCurrentUrl$() {
    return this.store.pipe(select(pipe(selectUrl)));
  }

  // PRODUCT LINKS

  categoryProductLinks$(categoryId: string | Observable<string>) {
    return toObservable(categoryId).pipe(
      switchMap(id =>
        this.store.pipe(
          select(getCategoryProductLinks(id)),
          tap(links => {
            if (!links) {
              this.store.dispatch(loadCategoryLinks({ categoryId: id }));
            }
          })
        )
      )
    );
  }

  /* DIAS CUSTOMIZATION ENDS */
}
